<template>
    <v-row justify="center" align="center">
        <v-col cols="12" sm="12" md="12">
            <!-- Header Section -->
            <v-row>
                <v-col cols="12" sm="12" md="6">
                    <h1>Master Data</h1>
                </v-col>
                <v-col cols="12" sm="12" md="6">
                    <v-text-field
                        v-model="data.quickSearchVal"
                        :label="CommonLabelTextHelper.getLabelText.filterCurrentPage"
                        @keydown="onKeydownQuickSearch"
                    ></v-text-field>
                </v-col>
            </v-row>

            <!-- Type Selector -->
            <v-row>
                <v-col cols="12" sm="12" md="6">
                    <v-select
                        v-model="data.selectedType"
                        :items="data.typeOptions"
                        item-title="text"
                        item-value="value"
                        label="Select Master Data Type"
                        placeholder="Please select a type first"
                        variant="outlined"
                        density="comfortable"
                        :rules="[(v) => !!v || 'Type selection is required']"
                    ></v-select>
                </v-col>
            </v-row>

            <!-- Alert Messages -->
            <alert-message ref="errorAlert" :message="data.errorAlertMessage"></alert-message>

            <!-- Action Buttons and Table -->
            <v-card>
                <v-card-title>
                    <v-row>
                        <v-col cols="12" sm="12" md="6">
                            <v-btn class="mr-2" color="primary" @click="onCreateClick" :disabled="!data.selectedType">
                                {{ data.labelText?.createButtonText || 'Create' }}
                            </v-btn>
                            <v-btn class="mr-2" color="success" @click="onImportClick" :disabled="!data.selectedType">
                                {{ data.labelText?.importButtonText || 'Import' }}
                            </v-btn>
                            <v-btn class="mr-2" text @click="onRefreshClick" :disabled="!data.selectedType">
                                <v-icon>mdi-cached</v-icon>
                                Refresh
                            </v-btn>
                        </v-col>
                        <v-col cols="12" sm="12" md="6">
                            <v-spacer></v-spacer>
                            <export-to-csv
                                :data="data.records"
                                :csv-header="data.csvExportConfig.csvHeader"
                                :filename="data.csvExportConfig.filename"
                                :formatter="csvFormatter"
                            ></export-to-csv>
                        </v-col>
                    </v-row>
                </v-card-title>

                <v-card-text>
                    <!-- Display loading state -->
                    <v-progress-linear v-if="data.loading" indeterminate color="primary"></v-progress-linear>

                    <!-- Table -->
                    <v-table v-if="!data.loading">
                        <thead>
                            <tr>
                                <th class="text-left" v-for="header in data.headers" :key="header.value">
                                    {{ header.text }}
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="item in filteredData" :key="item[data.config.codeField]">
                                <td v-for="header in data.headers" :key="header.value">
                                    <template v-if="header.value !== 'action'">
                                        {{ formatCellValue(item, header) }}
                                    </template>
                                    <template v-else>
                                        <v-btn variant="text" icon="mdi-pencil" @click="onUpdateClick(item)"></v-btn>
                                    </template>
                                </td>
                            </tr>
                        </tbody>
                    </v-table>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>
</template>

<script lang="ts">
import { defineComponent, reactive, watch, computed } from 'vue';
import * as _ from 'lodash';
import { fetchWrapper } from '../../utils/FetchWrapper';
import { PathDefinition } from '../../utils/PathDefinition';
import AlertMessage from '../../components/AlertMessage.vue';
import ExportToCsv from '../../components/ExportToCsv.vue';
import { MasterDataHeaderHelper, MASTER_DATA_CONFIG } from '../../utils/headerHelper/MasterDataHeaderHelper';
import { MasterDataLabelTextHelper } from '../../utils/labelTextHelper/MasterDataLabelTextHelper';
import { CommonLabelTextHelper } from '../../utils/labelTextHelper/CommonLabelTextHelper';
import { CsvFormatHelper } from '../../utils/CsvFormatHelper';
import { TableFormatHelper } from '../../utils/TableFormatHelper';

export default defineComponent({
    name: 'MasterData',

    components: {
        ExportToCsv,
        AlertMessage
    },

    setup() {
        const data = reactive({
            selectedType: null as string | null,
            records: [] as any[],
            headers: [] as any[],
            loading: false,
            quickSearchVal: '',
            errorAlertMessage: '',
            config: null as any,
            labelText: null as any,
            csvExportConfig: {
                filename: '',
                csvHeader: []
            },
            typeOptions: [
                { text: 'Currency', value: 'currency' },
                { text: 'Marketplace', value: 'marketplace' },
                { text: 'Product Category', value: 'productCategory' }
            ]
        });

        // Computed property for filtered data based on quick search
        const filteredData = computed(() => {
            if (!data.quickSearchVal || data.quickSearchVal.trim() === '') {
                return data.records;
            }

            const searchTerm = data.quickSearchVal.toLowerCase();
            return data.records.filter((record: any) => {
                return Object.values(record).some((value: any) => {
                    if (value === null || value === undefined) return false;
                    return String(value).toLowerCase().includes(searchTerm);
                });
            });
        });

        // Load data for selected type
        const loadData = async () => {
            data.loading = true;
            data.errorAlertMessage = '';

            try {
                const requestUrl = `/api/staff/${data.config.apiPath}/list`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (response.success) {
                    // Access the data using the configured response list key
                    data.records = _.get(response, `result.${data.config.responseListKey}`, []);
                } else {
                    data.errorAlertMessage = response.errors || 'Failed to load data';
                }
            } catch (error) {
                console.error('Error loading master data:', error);
                data.errorAlertMessage = 'An unexpected error occurred while loading data';
            } finally {
                data.loading = false;
            }
        };

        // Watch for type changes
        watch(
            () => data.selectedType,
            (newType) => {
                if (!newType) {
                    data.records = [];
                    data.headers = [];
                    data.config = null;
                    data.labelText = null;
                    data.csvExportConfig = { filename: '', csvHeader: [] };
                    return;
                }
                data.config = MASTER_DATA_CONFIG[newType];
                data.labelText = MasterDataLabelTextHelper.getLabelTextForType(newType);
                data.headers = MasterDataHeaderHelper.getHeadersForType(newType);
                data.csvExportConfig = {
                    filename: `export${data.config.displayName.replace(' ', '')}.csv`,
                    csvHeader: MasterDataHeaderHelper.getCsvHeaderForType(newType)
                };
                loadData();
            }
        );

        return {
            CommonLabelTextHelper,
            data,
            filteredData
        };
    },

    methods: {
        onKeydownQuickSearch(evt: any) {
            if (evt.key === 'Enter') {
                // Quick search is handled by computed property
            }
        },

        async onRefreshClick() {
            await this.loadData();
        },

        async onCreateClick() {
            await this.$router.push({
                path: `/createMasterData/${this.data.selectedType}`
            });
        },

        async onImportClick() {
            await this.$router.push({
                path: `/importMasterData/${this.data.selectedType}`
            });
        },

        async onUpdateClick(item: any) {
            const code = item[this.data.config.codeField];
            await this.$router.push({
                path: `/updateMasterData/${this.data.selectedType}/${code}`
            });
        },

        async loadData() {
            this.data.loading = true;
            this.data.errorAlertMessage = '';

            try {
                const requestUrl = `/api/staff/${this.data.config.apiPath}/list`;
                const response = await fetchWrapper.get(requestUrl, '');

                if (response.success) {
                    // Access the data using the configured response list key
                    this.data.records = _.get(response, `result.${this.data.config.responseListKey}`, []);
                } else {
                    this.data.errorAlertMessage = response.errors || 'Failed to load data';
                    this.$refs.errorAlert?.showAlert();
                }
            } catch (error) {
                console.error('Error loading master data:', error);
                this.data.errorAlertMessage = 'An unexpected error occurred while loading data';
                this.$refs.errorAlert?.showAlert();
            } finally {
                this.data.loading = false;
            }
        },

        formatCellValue(item: any, header: { value: string; format?: string }) {
            if (header.value === 'action') {
                return '';
            }
            return TableFormatHelper.formatDateTimeCellValue(item, header);
        },

        csvFormatter: function (value: unknown, key: string, row: Record<string, unknown>): unknown {
            return CsvFormatHelper.formatDateTimeCsvValue(value as string, key);
        }
    }
});
</script>

<style scoped></style>
